import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import PaymentService from '../services/payment.service';
import toast from 'react-hot-toast';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

const PaymentCallback = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const [status, setStatus] = useState('verifying'); // verifying, success, failed

    useEffect(() => {
        const reference = searchParams.get('clientReference') || searchParams.get('reference') || searchParams.get('trxref');

        if (reference) {
            startPolling(reference);
        } else {
            setStatus('failed');
            toast.error('No payment reference found');
        }
    }, [searchParams]);

    const startPolling = (reference) => {
        let attempts = 0;
        const maxAttempts = 24; // 2 minutes with 5s intervals

        const poll = async () => {
            try {
                const saveCard = localStorage.getItem('save_card') === 'true';
                const response = await PaymentService.verifyPayment({ reference, save_card: saveCard });
                
                // Assuming verifyPayment returns response directly from axios
                const data = response.data || response;
                
                if (data.status === 'completed') {
                    localStorage.removeItem('save_card');
                    setStatus('success');
                    toast.success('Payment successful!');
                    setTimeout(() => {
                        navigate('/orders');
                    }, 3000);
                    return; // Stop polling
                } else if (data.status === 'failed') {
                    setStatus('failed');
                    toast.error('Payment failed');
                    return; // Stop polling
                }

                // If still pending
                attempts++;
                if (attempts >= maxAttempts) {
                    setStatus('pending');
                    toast.error('Payment verification is taking too long. Please check your orders later.');
                    setTimeout(() => {
                        navigate('/orders');
                    }, 3000);
                    return;
                }

                setTimeout(poll, 5000);
            } catch (error) {
                console.error(error);
                // We might want to keep polling on network error, but for now we'll fail
                attempts++;
                if (attempts >= maxAttempts) {
                    setStatus('failed');
                    toast.error('Payment verification failed');
                } else {
                    setTimeout(poll, 5000);
                }
            }
        };

        poll();
    };

    return (
        <div className="min-h-[60vh] flex flex-col items-center justify-center p-4">
            {status === 'verifying' && (
                <div className="text-center space-y-4">
                    <Loader2 className="w-16 h-16 animate-spin text-zinc-900 mx-auto" />
                    <h2 className="text-2xl font-bold">Verifying Payment...</h2>
                    <p className="text-zinc-500">Please wait while we confirm your transaction.</p>
                </div>
            )}

            {status === 'pending' && (
                <div className="text-center space-y-4">
                    <Loader2 className="w-16 h-16 animate-spin text-yellow-500 mx-auto" />
                    <h2 className="text-2xl font-bold text-yellow-600">Payment Pending</h2>
                    <p className="text-zinc-500">Your payment is still processing. We will notify you once it's completed. Redirecting to orders...</p>
                </div>
            )}

            {status === 'success' && (
                <div className="text-center space-y-4">
                    <CheckCircle className="w-16 h-16 text-green-500 mx-auto" />
                    <h2 className="text-2xl font-bold text-green-600">Payment Successful!</h2>
                    <p className="text-zinc-500">Your order has been confirmed. Redirecting to orders...</p>
                    <button
                        onClick={() => navigate('/orders')}
                        className="px-6 py-2 bg-zinc-900 text-white rounded-lg font-medium hover:bg-zinc-800"
                    >
                        Go to Orders
                    </button>
                </div>
            )}

            {status === 'failed' && (
                <div className="text-center space-y-4">
                    <XCircle className="w-16 h-16 text-red-500 mx-auto" />
                    <h2 className="text-2xl font-bold text-red-600">Payment Failed</h2>
                    <p className="text-zinc-500">We couldn't verify your payment. Please contact support if you were charged.</p>
                    <button
                        onClick={() => navigate('/cart')}
                        className="px-6 py-2 bg-zinc-900 text-white rounded-lg font-medium hover:bg-zinc-800"
                    >
                        Return to Cart
                    </button>
                </div>
            )}
        </div>
    );
};

export default PaymentCallback;
